import { useCallback } from 'react'

import type {
  EngagementReplySubmitOutcome,
  EngagementReplySubmitStrategy,
  EngagementReplySubmitStrategyDeps,
} from '../../../components/EngagementReplyForm/hooks/types'
import { useReplyToEngagement } from '../../../hooks/engagement/useReplyToEngagement'
import { useMentionData } from '../../context/MentionContext'
import type { MentionData } from '../../context/MentionContext'

type MentionReply = MentionData

/**
 * The channel shape the strategy receives, still the full codegen `Channel`
 * behind the shared reply-form seam. Collapses when that seam goes
 * fragment-first.
 */
type ReplyChannel = EngagementReplySubmitStrategyDeps<MentionReply>['channel']

/**
 * Builds the just-sent reply as a `Mention` so `MentionLastReplies` can render
 * it immediately, without invalidating the list query.
 *
 * Comments echo the real reply back via `Comment.lastReplies`; the mention
 * mutation returns the *parent* (now `replied`) and `Mention` has no
 * `lastReplies` field, so the reply is reconstructed from local data instead.
 * Inherits the parent's channel/org/service context (spread), then overrides
 * the reply-specific fields. The author is the channel (own reply), and media
 * is dropped — the parent's assets don't belong to the reply.
 */
const buildOptimisticReply = ({
  parent,
  channel,
  text,
  id,
  publishedAt,
}: {
  parent: MentionData
  channel: ReplyChannel
  text: string
  id: string
  publishedAt: string
}): MentionData => ({
  ...parent,
  id,
  parentId: parent.id,
  text,
  isOwnEngagement: true,
  status: 'replied',
  assets: [],
  suggestions: [],
  reactions: [],
  author: {
    __typename: 'EngagementAuthor',
    name: channel.name,
    username: channel.name,
    avatar: channel.avatar,
    letterAvatar: null,
  },
  publishedAt,
  createdAt: publishedAt,
})

/**
 * Mentions' reply submit strategy: replies via the unified `replyToEngagement`
 * mutation. The mutation returns only the parent (now `replied`), so the
 * just-sent reply is synthesized locally and handed back for optimistic
 * display — no server echo and no out-of-band reconciliation.
 *
 * @see useEngagementReplyForm
 */
export const useMentionReplySubmitStrategy = ({
  channel,
}: EngagementReplySubmitStrategyDeps<MentionReply>): EngagementReplySubmitStrategy<MentionReply> => {
  const mention = useMentionData()
  const { replyToEngagement } = useReplyToEngagement()

  const submit = useCallback(
    async (
      finalText: string,
    ): Promise<EngagementReplySubmitOutcome<MentionReply>> => {
      const result = await replyToEngagement({
        engagementId: mention.id,
        engagementType: 'mention',
        text: finalText,
      })

      if (result.success) {
        const publishedAt = new Date().toISOString()
        const optimisticReply = buildOptimisticReply({
          parent: mention,
          channel,
          text: finalText,
          // The mutation returns the parent, not the reply, so there's no real
          // reply id to key on — a synthetic one keeps the row stable locally.
          id: `optimistic-reply-${mention.id}-${publishedAt}`,
          publishedAt,
        })
        return { status: 'published', reply: optimisticReply }
      }

      return {
        status: 'error',
        message: `Failed to send the reply. ${result.error ?? ''}`.trim(),
      }
    },
    [mention, channel, replyToEngagement],
  )

  return { submit }
}
